import React, { useEffect, useState, useRef } from 'react';
import {
  CaretDown,
  MagnifyingGlass,
  X,
  Plus,
  Trash,
} from '@phosphor-icons/react';
import { BounceLoader, MoonLoader } from 'react-spinners';
import colors from '@Assets/colors';
import { analyzerAPI } from '@Lib/axios/analyzer';
import { AhaTicketGeneratorPrompt, promptAnalyzer } from '@Models/types';
import showToast from '@Common/toast/showToast';
import { useAuth } from '@Context/CognitoAuthProvider';
import { promptToolboxAPI } from '@Lib/axios/prompt-toolbox';
interface Option<T> {
  label: string;
  value: T;
}

interface SelectProps<T> {
  options: Option<T>[];
  onChange: (value: Option<T> | null) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  type: string;
  onShowPrompt: () => void;
  onShowView: () => void;
  value?: Option<T> | null;
  onPromptsUpdated?: (prompts: promptAnalyzer[]) => void;
}

const PromptSelect = <T,>({
  options,
  placeholder,
  loading = false,
  disabled = false,
  type,
  onShowPrompt,
  onShowView,
  onChange,
  value = null,
  onPromptsUpdated,
}: SelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option<T> | null>(value);
  const [fetchedOptions, setFetchedOptions] = useState<
    Option<promptAnalyzer>[]
  >([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [confirmingPromptId, setConfirmingPromptId] = useState<string | null>(
    null
  );
  const [deletingPromptId, setDeletingPromptId] = useState<string | null>(null);
  const dropDownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const analyzer = analyzerAPI(logout);
  const promptToolBox = promptToolboxAPI(logout);

  useEffect(() => {
    if (user?.admin) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, []);

  useEffect(() => {
    const fetchPrompts = async () => {
      setLoadingOptions(true);
      try {
        const prompts = await analyzer.getAnalyzerPrompts();
        const opts: Option<promptAnalyzer>[] = prompts.map((p) => ({
          label: p.name,
          value: p,
        }));
        console.log('Fetched prompts from API:', opts);
        setFetchedOptions(opts);
      } catch (err) {
        console.error('Error fetching prompts:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchPrompts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropDownRef.current &&
        !dropDownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setConfirmingPromptId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value !== selectedOption) {
      setSelectedOption(value);
    }
  }, [value]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => setSearch('');

  const handleOptionClick = (option: Option<T>) => {
    setSelectedOption(option);
    setIsOpen(false);
    onChange(option);
    onShowView();
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleAddPromptsClick = () => {
    onShowPrompt();
    setIsOpen(false);
  };

  const handleConfirmDelete = async (prompt: promptAnalyzer) => {
    setConfirmingPromptId(null);
    setDeletingPromptId(prompt.id);
    try {
      let prompts: promptAnalyzer[] | AhaTicketGeneratorPrompt[];
      if (type === 'generator prompt') {
        await promptToolBox.deleteAhaTicketGeneratorPrompt(prompt.id);
        prompts = await promptToolBox.getAhaTicketGeneratorPrompts();
      } else {
        await analyzer.deleteAnalyzerPrompt(prompt.id);
        prompts = await analyzer.getAnalyzerPrompts();
      }
      showToast.success(`Prompt "${prompt.name}" deleted successfully!`);

      const opts: Option<promptAnalyzer>[] = prompts.map((p) => ({
        label: p.name,
        value: p,
      }));
      setFetchedOptions(opts);
      if (onPromptsUpdated) {
        onPromptsUpdated(prompts);
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
      showToast.error('Failed to delete prompt.');
    } finally {
      setDeletingPromptId(null);
    }
  };

  const initiateDelete = (prompt: promptAnalyzer) => {
    setConfirmingPromptId(prompt.id);
  };

  const finalOptions: Option<T>[] =
    options && options.length > 0
      ? options
      : (fetchedOptions as unknown as Option<T>[]);
  const filteredOptions = finalOptions.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );
  const overallLoading = loading || loadingOptions;

  return (
    <div
      ref={dropDownRef}
      role="combobox"
      aria-expanded={isOpen ? 'true' : 'false'}
      className="relative w-full"
    >
      <div
        data-testid="test-select-toggle"
        className={`flex ${overallLoading ? 'cursor-auto' : 'cursor-pointer'} items-center justify-between rounded-md px-4 py-2 ${
          isOpen ? 'bg-primary-700' : 'bg-primary-100'
        }`}
        onClick={toggleDropdown}
      >
        <span
          className={`${isOpen ? 'text-white' : 'text-primary-700'} font-inter text-[0.9rem] font-semibold ${
            overallLoading && 'animate-pulse'
          }`}
        >
          {selectedOption
            ? selectedOption.label
            : placeholder || 'Select an option'}
        </span>
        {overallLoading ? (
          <BounceLoader
            data-testid="Loader"
            size={20}
            color={colors.brandWarning[500]}
          />
        ) : (
          <CaretDown
            weight="bold"
            className={`transform ${isOpen ? 'rotate-180 text-white' : 'text-primary-700'}`}
          />
        )}
      </div>
      {isOpen && (
        <div
          role="listbox"
          className="absolute z-10 mt-1 w-full rounded border bg-white p-2 shadow-lg"
        >
          <div className="flex justify-center gap-2">
            <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-4 py-2">
              <MagnifyingGlass size={20} weight="bold" />
              <input
                data-testid="test-select-search-input"
                type="text"
                className="w-full border-gray-300 font-inter text-sm focus:outline-none"
                placeholder={`Search ${type}`}
                value={search}
                onChange={handleSearch}
              />
              <X
                weight="bold"
                className="cursor-pointer hover:text-brandRed-500"
                onClick={clearSearch}
              />
            </div>
            {isAdmin && (
              <button
                onClick={handleAddPromptsClick}
                className="group flex w-12 items-center justify-center rounded-full border hover:border-primary-600"
              >
                <Plus
                  weight="bold"
                  className="cursor-pointer group-hover:text-primary-600"
                />
              </button>
            )}
          </div>
          <ul className="custom-scroll max-h-60 overflow-auto px-2 py-1 font-inter text-sm">
            {overallLoading ? (
              <span className="flex h-full w-full flex-col items-center justify-center gap-y-4 p-5">
                <MoonLoader size={50} color="#071D49" />
                <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600">
                  {type}s Loading
                </span>
              </span>
            ) : (
              <>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => {
                    const promptVal = option.value as unknown as promptAnalyzer;
                    return (
                      <li
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOptionClick(option);
                        }}
                        className="flex cursor-pointer items-center justify-between rounded-md bg-white px-4 py-2 text-primary-700 transition-colors hover:bg-primary-100"
                      >
                        <div className="flex-1 cursor-pointer">
                          {option.label}
                        </div>
                        {isAdmin && (
                          <>
                            {confirmingPromptId === promptVal.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConfirmDelete(promptVal);
                                  }}
                                  className="text-xs font-extrabold text-green-600"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmingPromptId(null);
                                  }}
                                  className="text-xs font-extrabold text-gray-600"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : deletingPromptId === promptVal.id ? (
                              <MoonLoader size={16} color="#333333" />
                            ) : promptVal.id !== '000000000000000000000000' ? (
                              <Trash
                                size={18}
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  initiateDelete(promptVal);
                                }}
                              />
                            ) : (
                              <div></div>
                            )}
                          </>
                        )}
                      </li>
                    );
                  })
                ) : (
                  <li className="px-4 py-2 text-gray-500">
                    No prompts available
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PromptSelect;
